import { VStack, Text, Input, InputGroup, InputLeftElement, HStack, IconButton, useColorModeValue, Spinner, Center, useToast, Box, Flex, Badge, Button, Select } from '@chakra-ui/react';
import { useState, useMemo, useEffect, useRef } from 'react';
import { SearchIcon } from '@chakra-ui/icons';
import { FiX } from 'react-icons/fi';
import ContentRow from '../components/ContentRow';
import IconWrapper from '../components/IconWrapper';
import { searchContent, getTopContent, type SearchResult, type SearchFilters } from '../services/searchService';
import { GENRES } from '../constants/genres';
import AdSenseSlot from '../components/AdSenseSlot';
import { ADSENSE_CLIENT_ID, ADSENSE_SLOTS } from '../constants/adsense';


const Search: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>({});
  const [tagInput, setTagInput] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedGenre, setSelectedGenre] = useState<string>('all');
  const debounceRef = useRef<number | undefined>(undefined);
  const toast = useToast();
  
  const titleColor = useColorModeValue('gray.900', 'white');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const inputFocusBg = useColorModeValue('gray.100', 'gray.600');
  const inputColor = useColorModeValue('gray.900', 'white');
  const placeholderColor = useColorModeValue('gray.500', 'gray.400');

  // Perform search API
  const performSearch = async (term: string) => {
    if (term.trim() === '') {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }
    setLoading(true);
    try {
      const searchFilters: SearchFilters = {
        ...filters,
        genre: selectedGenre !== 'all' ? selectedGenre : undefined,
        tags: selectedTags.length > 0 ? selectedTags : undefined
      } as SearchFilters;
      const { results } = await searchContent(term, searchFilters);
      setSearchResults(results);
      setHasSearched(true);
    } catch (error) {
      console.error('Search error:', error);
      toast({
        title: 'Search failed',
        description: 'Unable to search content. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Debounce search input
  useEffect(() => {
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      if (searchTerm.trim()) {
        performSearch(searchTerm);
      }
    }, 400);
    return () => {
      window.clearTimeout(debounceRef.current);
    };
  }, [searchTerm, selectedGenre, selectedTags]);

  // Add tag functionality
  const addTag = () => {
    if (tagInput.trim() && !selectedTags.includes(tagInput.trim())) {
      setSelectedTags([...selectedTags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      addTag();
    }
  };

  // Load trending content on component mount
  useEffect(() => {
    const loadTrending = async () => {
      try {
        const trending = await getTopContent('all', 'week', 20);
        setSearchResults(trending);
      } catch (error) {
        console.error('Error loading trending content:', error);
      }
    };
    loadTrending();
  }, []);

  // Memoized search results for performance
  const memoizedResults = useMemo(() => {
    if (!hasSearched || searchResults.length === 0) return [];
    
    return [{
      title: `Search Results for "${searchTerm}" (${searchResults.length} found)`,
      items: searchResults.map((result: SearchResult) => ({
        id: result.id,
        title: result.title,
        type: result.category,
        thumbnail: result.thumbnail
      }))
    }];
  }, [searchResults, searchTerm, hasSearched]);

  if (loading) {
    return (
      <Center minH="50vh">
        <Spinner size="xl" color="purple.400" />
      </Center>
    );
  }


  // Use trending content from Firebase instead of movies
  const contentRows = memoizedResults;

  return (
    <VStack gap={8} align="stretch" maxW="1200px" mx="auto">
      <AdSenseSlot
        client={ADSENSE_CLIENT_ID}
        slot={ADSENSE_SLOTS.SEARCH_SIDEBAR}
        format="auto"
        fullWidthResponsive
        style={{ margin: '8px 0' }}
      />
      <VStack gap={6} align="stretch">
        <Text fontSize="2xl" fontWeight="bold" color={titleColor}>
          Search
        </Text>
        
        <InputGroup size="lg">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color={placeholderColor} />
          </InputLeftElement>
          <Input
            placeholder="Search movies, series, books, creators..."
            bg={inputBg}
            border="none"
            color={inputColor}
            _placeholder={{ color: placeholderColor }}
            _focus={{ bg: inputFocusBg }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>

        {/* Genre Filter */}
        <HStack>
          <Select
            placeholder="All genres"
            value={selectedGenre}
            onChange={(e) => setSelectedGenre(e.target.value || 'all')}
            maxW="300px"
            bg={inputBg}
            border="none"
            color={inputColor}
            _focus={{ bg: inputFocusBg }}
          >
            {GENRES.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </Select>
          {hasSearched && (
            <Button size="sm" variant="outline" onClick={() => { setSelectedGenre('all'); setSearchTerm(''); setSearchResults([]); setHasSearched(false); }}>
              Clear
            </Button>
          )}
        </HStack>

        {/* Tag Filtering */}
        <Box>
          <Text fontSize="lg" fontWeight="semibold" mb={3} color={titleColor}>
            Filter by Tags
          </Text>
          <VStack align="stretch" gap={3}>
            <HStack>
              <Input
                placeholder="Add tags to filter by..."
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleKeyPress}
                bg={inputBg}
                border="none"
                color={inputColor}
                _placeholder={{ color: placeholderColor }}
                _focus={{ bg: inputFocusBg }}
              />
              <Button onClick={addTag} colorScheme="purple" size="sm">
                Add
              </Button>
            </HStack>
            
            {selectedTags.length > 0 && (
              <Flex wrap="wrap" gap={2}>
                {selectedTags.map((tag, index) => (
                  <Badge
                    key={index}
                    colorScheme="blue"
                    variant="subtle"
                    px={3}
                    py={1}
                    borderRadius="full"
                    display="flex"
                    alignItems="center"
                    gap={2}
                  >
                    {tag}
                    <IconButton
                      aria-label={`Remove ${tag} tag`}
                      icon={<IconWrapper icon={FiX} size={12} />}
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => removeTag(tag)}
                    />
                  </Badge>
                ))}
              </Flex>
            )}
          </VStack>
        </Box>
      </VStack>
      
      {hasSearched ? (
        // Show search results
        memoizedResults.length > 0 ? (
          memoizedResults.map((row: any, index: number) => (
            <ContentRow
              key={index}
              title={row.title}
              items={row.items}
            />
          ))
        ) : (
          <Center py={8}>
            <Text fontSize="lg" color={placeholderColor}>
              No results found for "{searchTerm}"
            </Text>
          </Center>
        )
      ) : (
        // Show default content rows when not searching
        contentRows.map((row: any, index: number) => (
          <ContentRow
            key={index}
            title={row.title}
            items={row.items}
          />
        ))
      )}
    </VStack>
  );
};

export default Search;
